import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

/**
 * Metadata for the partner's own live-lookup API key. The full plaintext key is
 * never returned here — it is shown once by the regenerate mutation only.
 */
export interface PartnerApiKeyMetadata {
  hasKey: boolean;
  /**
   * Whether the partner has ever been shown a usable plaintext key (i.e. has
   * generated/regenerated at least once). An admin grant provisions a key whose
   * plaintext is discarded, so a freshly-flagged partner has `hasKey: true` but
   * `activated: false` and no usable key until they generate one. The page uses
   * this to show a "generate to get started" first-run prompt instead of a dead
   * "you have a key" panel. Optional for forward-compatibility: when the backend
   * omits it, the page falls back to treating an existing key as active.
   */
  activated?: boolean;
  keyPrefix: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
}

/**
 * Response from regenerate: the plaintext key, shown to the partner exactly once.
 * Persisting it client-side would defeat the hashed-at-rest design, so the UI must
 * surface it immediately and never store it.
 */
export interface RegeneratePartnerApiKeyResponse {
  key: string;
  keyPrefix: string;
  createdAt: string;
}

/**
 * Partner self-service API for managing the caller's own live-lookup key. Gated
 * server-side to accounts whose roles include `partner-api`; uses the normal
 * user session (cookie/JWT), so requiresAuth stays true (a 401 here means the
 * session expired, and the standard logout-on-401 behavior is correct).
 */
export const partnerApi = createApi({
  reducerPath: 'partnerApi',
  baseQuery: baseQuery,
  tagTypes: ['PartnerApiKey'],
  endpoints: (builder) => ({
    getMyApiKey: builder.query<PartnerApiKeyMetadata, void>({
      query: () => ({
        url: '/partner/api-key/me',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['PartnerApiKey'],
    }),
    regenerateApiKey: builder.mutation<RegeneratePartnerApiKeyResponse, void>({
      query: () => ({
        url: '/partner/api-key/regenerate',
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['PartnerApiKey'],
    }),
    revokeApiKey: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: '/partner/api-key/me',
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['PartnerApiKey'],
    }),
  }),
});

export const {
  useGetMyApiKeyQuery,
  useRegenerateApiKeyMutation,
  useRevokeApiKeyMutation,
} = partnerApi;
